import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "../i18n/LanguageContext";

const LANGS = [
  { code: "en", label: "EN" },
  { code: "el", label: "ΕΛ" },
  { code: "ru", label: "RU" },
];

const CyprusClock = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () =>
      setTime(new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Nicosia" }).format(new Date()));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="hidden xl:flex items-center gap-2 font-mono text-xs tracking-[0.18em] text-[#526071]">
      <span className="w-1.5 h-1.5 rounded-lg bg-[#4A5D4E] animate-pulse" />
      CYPRUS {time}
    </span>
  );
};

export const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-link-home" },
    { to: "/about", label: t.nav.about, testid: "nav-link-about" },
    { to: "/services", label: t.nav.services, testid: "nav-link-services" },
    { to: "/contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#FBF9F5]/90 border-b border-[#E3DED4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-2 sm:gap-6">
        <Link to="/" className="flex items-center gap-2.5 sm:gap-3 shrink-0" data-testid="brand-logo" onClick={() => setOpen(false)}>
          <Logo size={32} />
          <span className="leading-none">
            <span className="block font-serif font-semibold text-[12px] sm:text-[15px] tracking-[0.12em] sm:tracking-[0.14em] text-[#142D44]">HORIZON ADVISORS</span>
            <span className="hidden sm:block font-mono text-[9px] tracking-[0.22em] text-[#8C6B4F] mt-1">CYPRUS · BUSINESS & PROPERTY</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.12em] font-semibold transition-colors duration-200 ${isActive ? "text-[#B85D2A]" : "text-[#4A5568] hover:text-[#142D44]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4">
          <CyprusClock />
          <div className="flex items-center border border-[#E3DED4] rounded-lg p-0.5 bg-white/60" data-testid="language-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-btn-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2 sm:px-2.5 py-1 rounded-lg text-[11px] font-mono tracking-wider transition-colors duration-200 ${
                  lang === l.code ? "bg-[#1E3A5F] text-[#FBF9F5]" : "text-[#526071] hover:text-[#142D44]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-consultation-btn"
            onClick={() => { setOpen(false); navigate("/contact"); }}
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-lg bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            className="lg:hidden text-[#142D44] p-1"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <Menu size={22} className="hidden" /> : null}
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E3DED4] bg-[#FBF9F5] px-5 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `font-serif text-2xl ${isActive ? "text-[#B85D2A]" : "text-[#142D44]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            onClick={() => { setOpen(false); navigate("/contact"); }}
            className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-lg bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium"
          >
            {t.nav.cta}
          </button>
        </div>
      )}
    </header>
  );
};
